import React from 'react';
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Link2 } from 'lucide-react';
import { AgentConfig } from '../config/agents';

interface FooterProps {
  agent: AgentConfig;
}

export default function Footer({ agent }: FooterProps) {
  const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname.includes('staging');

  return (
    <footer className="bg-navy-900 text-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-xl font-semibold mb-4">Retter & Company</h3>
            <p className="text-sm text-gray-300">Sotheby's International Realty</p>
            <p className="text-xs text-gray-400 mt-2">
              Each office is independently owned and operated.
            </p>
            <a 
              href="https://www.rcsothebysrealty.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-6"
            >
              <img 
                src="/rnc.png" 
                alt="Retter & Company Logo" 
                className="h-12 w-auto hover:opacity-80 transition-opacity duration-200"
              />
            </a>
            <div className="flex space-x-4 mt-6">
              {agent.socialLinks.facebook && (
                <a
                  href={agent.socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                  aria-label="Facebook Profile"
                >
                  <Facebook className="w-6 h-6" />
                </a>
              )}
              {agent.socialLinks.instagram && (
                <a
                  href={agent.socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                  aria-label="Instagram Profile"
                >
                  <Instagram className="w-6 h-6" />
                </a>
              )}
              {agent.socialLinks.linkedin && (
                <a
                  href={agent.socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                  aria-label="LinkedIn Profile"
                >
                  <Linkedin className="w-6 h-6" />
                </a>
              )}
              {agent.socialLinks.zillow && (
                <a
                  href={agent.socialLinks.zillow}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                  aria-label="Zillow Profile"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 23.283 25.577"
                    className="w-6 h-6"
                    fill="currentColor"
                  >
                    <g fillRule="nonzero">
                      <path d="m15.743 6.897c.117-.026.169.013.24.091.403.448 1.691 2.021 2.041 2.45.065.078.02.163-.032.208-2.6 2.028-5.493 4.901-7.105 6.955-.032.046-.006.046.02.039 2.808-1.209 9.405-3.14 12.376-3.679v-3.763l-11.628-9.198-11.648 9.191v4.114c3.607-2.144 11.953-5.466 15.736-6.408z" />
                      <path d="m6.279 22.705c-.097.052-.176.039-.254-.039l-2.171-2.587c-.058-.072-.065-.111.013-.221 1.678-2.457 5.103-6.286 7.287-7.904.039-.026.026-.059-.02-.039-2.275.741-8.742 3.523-11.134 4.875v8.787h23.277v-8.462c-3.172.539-12.675 3.367-16.998 5.59z" />
                    </g>
                  </svg>
                </a>
              )}
              <a
                href={agent.socialLinks.retter}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                aria-label="Retter & Company Profile"
              >
                <Link2 className="w-6 h-6" />
              </a>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <nav className="space-y-2">
              <a href="/" className="block text-gray-300 hover:text-white">Home</a>
              <a href="/about" className="block text-gray-300 hover:text-white">About</a>
              <a href="/contact" className="block text-gray-300 hover:text-white">Contact</a>
            </nav>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Get In Touch</h3>
            <div className="space-y-3">
              <p className="flex items-start text-gray-300">
                <MapPin className="w-5 h-5 mr-2 flex-shrink-0 mt-1" />
                {agent.office.address}<br />
                {agent.office.city}, {agent.office.state} {agent.office.zip}<br />
                {agent.office.country}
              </p>
              <p className="flex items-center text-gray-300">
                <Phone className="w-5 h-5 mr-2" />
                {agent.phone}
              </p>
              <p className="flex items-center text-gray-300">
                <Mail className="w-5 h-5 mr-2" />
                {agent.email}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Toolbox Icon */}
      <a
        href="https://md.retterco.com/home"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-4 right-4 text-[20px] opacity-40 hover:opacity-100 transition-opacity duration-200"
        aria-label="Visit Toolbox"
      >
        🤖
      </a>
    </footer>
  );
}